import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT } from './constants';
import type { GameState, Position } from '@/types/game';
import { puzzles } from '@/game/puzzles';
import type { PuzzleData } from '@/types/puzzle';
import { getPuzzleGrid } from '@/types/puzzle';

interface WarpOverlay3DProps {
  gameState: GameState;
  totalCols: number;
  totalRows: number;
  onTileClick: (position: Position) => void;
}

export default function WarpOverlay3D({ gameState, totalCols, totalRows, onTileClick }: WarpOverlay3DProps) {
  const pulseMeshes = useRef<THREE.Mesh[]>([]);

  // Compute valid warp tiles (walkable, non-wall, non-lava)
  const warpTiles = useMemo(() => {
    const isInfinite = gameState.gameMode === 'endless' && gameState.infiniteGrid;
    const puzzle = isInfinite ? null : (puzzles[gameState.level] as PuzzleData);
    if (!puzzle && !isInfinite) return [];

    const puzzleGrid = isInfinite ? gameState.infiniteGrid! : getPuzzleGrid(puzzle!);
    const tiles: Array<{ col: number; row: number }> = [];

    puzzleGrid.forEach((rowStr, rowIndex) => {
      rowStr.split('').forEach((cell, colIndex) => {
        const c = cell.toLowerCase();
        // Walkable: empty, start, end, stop, teleporter, powerup
        if (c !== 'x' && c !== 'l') {
          // Don't warp to current position
          if (colIndex !== gameState.position[0] || rowIndex !== gameState.position[1]) {
            tiles.push({ col: colIndex, row: rowIndex });
          }
        }
      });
    });

    return tiles;
  }, [gameState.level, gameState.position, gameState.gameMode, gameState.infiniteGrid]);

  // Pulse animation
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    pulseMeshes.current.forEach((mesh) => {
      if (mesh) {
        const mat = mesh.material as THREE.MeshStandardMaterial;
        mat.opacity = 0.25 + Math.sin(t * 3) * 0.15;
      }
    });
  });

  const y = FLOOR_HEIGHT + 0.13;

  return (
    <group>
      {warpTiles.map((tile, i) => {
        const x = (tile.col - (totalCols - 1) / 2) * TILE_SIZE;
        const z = (tile.row - (totalRows - 1) / 2) * TILE_SIZE;

        return (
          <mesh
            key={`${tile.col}-${tile.row}`}
            ref={(el) => { if (el) pulseMeshes.current[i] = el; }}
            position={[x, y, z]}
            rotation={[-Math.PI / 2, 0, 0]}
            onClick={(e) => {
              e.stopPropagation();
              onTileClick([tile.col, tile.row]);
            }}
          >
            <planeGeometry args={[TILE_SIZE * 0.85, TILE_SIZE * 0.85]} />
            <meshStandardMaterial
              color="#AB47BC"
              emissive="#AB47BC"
              emissiveIntensity={0.3}
              transparent
              opacity={0.35}
              side={2}
            />
          </mesh>
        );
      })}
    </group>
  );
}
